/// <amd-module name="Analytics/Boards/BurndownQueries/UnestimatedWorkItemsQueries" />
import { FieldFilter, WorkItemFieldDescriptor } from "Analytics/Boards/BoardsQueries/DataContracts";
import { TeamScope } from "Analytics/Boards/BoardsQueries/DataContracts";
import { WitFieldUtilities } from "Analytics/Boards/BoardsQueries/WitFieldUtilities";
import { AnalyticsODataVersion, ODataQueryOptions } from "Analytics/Common/ODataQueryOptions";
import * as PublicProjectsQueryHelper from "Analytics/Common/Utilities/PublicProjectsQueryHelper";
import { QueryUtilities } from "Analytics/Common/Utilities/QueryUtilities";
import { latestODataVersion } from "Analytics/Common/Utilities/QueryUtilities";
import { IVssPageContext } from "VSS/Platform/Context";
import { uniqueSort } from "VSS/Platform/Util/Array";
import { BurndownQueryBase } from "./BurndownQueryBase";

export interface ODataWorkItem {
    WorkItemId: string;
}

export class UnestimatedWorkItemIdsQuery extends BurndownQueryBase<string[]> {
    constructor(
        pageContext: IVssPageContext,
        teamScopes: TeamScope[],
        filters: FieldFilter[],
        workItemTypes: string[],
        workItemFieldDescriptors: WorkItemFieldDescriptor[],
        effortField: string,
        showResolvedItemsAsCompleted = false,
        commandName?: string
    ) {
        super(
            generateQueryOptions(
                pageContext,
                latestODataVersion,
                false /* return count */,
                teamScopes,
                filters,
                workItemTypes,
                workItemFieldDescriptors,
                effortField,
                showResolvedItemsAsCompleted
            ),
            commandName
        );
    }

    public getQueryName(): string {
        return "UnestimatedWorkItemIdsQuery";
    }

    protected interpretQueryResults(data: { value: ODataWorkItem[] }): string[] {
        return data.value.map(value => value.WorkItemId);
    }
}

export interface ODataWorkItemCount {
    Count: number;
}

export class UnestimatedWorkItemsCountQuery extends BurndownQueryBase<number> {
    constructor(
        pageContext: IVssPageContext,
        teamScopes: TeamScope[],
        filters: FieldFilter[],
        workItemTypes: string[],
        workItemFieldDescriptors: WorkItemFieldDescriptor[],
        effortField: string,
        showResolvedItemsAsCompleted = false,
        commandName?: string
    ) {
        super(
            generateQueryOptions(
                pageContext,
                latestODataVersion,
                true /* return count */,
                teamScopes,
                filters,
                workItemTypes,
                workItemFieldDescriptors,
                effortField,
                showResolvedItemsAsCompleted
            ),
            commandName
        );
    }

    public getQueryName(): string {
        return "UnestimatedWorkItemsCountQuery";
    }

    protected interpretQueryResults(data: { value: ODataWorkItemCount[] }): number {
        return data.value != null && data.value.length > 0 ? data.value[0].Count : 0;
    }
}

function generateQueryOptions(
    pageContext: IVssPageContext,
    axODataVersion: AnalyticsODataVersion,
    returnCount: boolean,
    teamScopes: TeamScope[],
    filters: FieldFilter[],
    workItemTypes: string[],
    workItemFieldDescriptors: WorkItemFieldDescriptor[],
    effortField: string,
    showResolvedItemsAsCompleted = false
): ODataQueryOptions {
    const teams = uniqueSort(teamScopes.map(scope => scope.teamId));
    const teamsClause = QueryUtilities.isInArray("t/TeamSK", teams, false /* useQuotes */);

    const projects = uniqueSort(teamScopes.map(scope => scope.projectId));
    const projectsClause = QueryUtilities.isInArray("ProjectSK", projects, false /* useQuotes */);

    const filtersClause = WitFieldUtilities.getFiltersClause(filters, workItemFieldDescriptors);

    const effortFieldAnalyticsName = WitFieldUtilities.getFieldODataPropertyName(effortField);

    const workItemTypesClause = QueryUtilities.isInArray("WorkItemType", workItemTypes, true /* useQuotes */);

    let $apply =
        `filter(` +
        `(${projectsClause})` +
        ` and Teams/any(t:${teamsClause})` +
        ` and (StateCategory ne 'Completed'`;

    if(showResolvedItemsAsCompleted){
        $apply += 
        ` and StateCategory ne 'Resolved'` +
        ` and State ne 'Resolved'`;
    }

    $apply += 
    `) and ${effortFieldAnalyticsName} eq null` +
    ` and (${workItemTypesClause})`;

    if (filtersClause) {
        $apply += ` and (${filtersClause})`;
    }

    $apply += ")";

    if (returnCount) {
        $apply += "/aggregate($count as Count)";
    }

    const $select = returnCount ? undefined : "WorkItemId";

    return PublicProjectsQueryHelper.forceProjectScoping(pageContext, projects, {
        entityType: "WorkItems",
        oDataVersion: axODataVersion,
        $apply: $apply,
        $select: $select
    });
}
